// InventorySection.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import FormInput from "../../../components/controls/FormInput";
import FormSelectBox from "../../../components/controls/FormSelectBox";
import TutorialModal from "../../../components/ui/TutorialModal";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

const InventorySection = ({
  formData,
  onChange,
  isService,
  priceTypeId,
  onInventoryModeChange,
}) => {
  const { t } = useTranslation();
  const [showTutorial, setShowTutorial] = useState(false);

  const hasOptionQuantities =
    Array.isArray(formData.productConfigurations) &&
    formData.productConfigurations[0]?.values?.some(
      (val) => val.quantity !== null && val.quantity !== undefined
    );

  const numericPriceTypeId = parseInt(priceTypeId, 10);

  const isFixedPrice = numericPriceTypeId === 5;
  const isPerHour = numericPriceTypeId === 1;
  const isPerPerson = numericPriceTypeId === 2;
  const isPerDay = numericPriceTypeId === 3;
  const isPerUnit = numericPriceTypeId === 4;

  const showInventoryFields = isPerHour || isPerPerson || isPerUnit;

  const tutorialSteps = [
    {
      title: t("tutorials.productsAndServicesCreation.inventoryGuideTitle"),
      content: t("tutorials.productsAndServicesCreation.inventoryGuideIntro"),
      image: "https://.../inventory-overview.png",
      tips: [
        t("tutorials.productsAndServicesCreation.tips", {
          returnObjects: true,
        }),
      ],
    },
    {
      title: t("tutorials.productsAndServicesCreation.totalQuantityTitle"),
      content: t("tutorials.productsAndServicesCreation.totalQuantityText"),
      image: "https://.../inventory-total.png",
    },
    {
      title: t("tutorials.productsAndServicesCreation.limitPerDayTitle"),
      content: t("tutorials.productsAndServicesCreation.limitPerDayText"),
      image: "https://.../inventory-limit-per-day.png",
    },
    {
      title: t("tutorials.productsAndServicesCreation.maxDurationTitle"),
      content: t("tutorials.productsAndServicesCreation.maxDurationText"),
      image: "https://.../inventory-max-duration.png",
    },
    {
      title: t("tutorials.productsAndServicesCreation.returnModeTitle"),
      content: t("tutorials.productsAndServicesCreation.returnModeText"),
      image: "https://.../inventory-return-mode.png",
    },
    {
      title: t("tutorials.productsAndServicesCreation.examplesTitle"),
      content: "",
      tips: [
        t("tutorials.productsAndServicesCreation.example1"),
        t("tutorials.productsAndServicesCreation.example2"),
        t("tutorials.productsAndServicesCreation.example3"),
        t("tutorials.productsAndServicesCreation.example4"),
      ],
    },
  ];

  if (!formData.productInventoryLimits) {
    formData.productInventoryLimits = {
      inventoryMode: "unlimited",
      totalQuantity: 0,
      maxDuration: 0,
      returnPolicy: "none",
    };
  }

  const openTutorialModal = () => {
    setShowTutorial(true);
  };

  let inventoryOptions = [];

  if (isPerHour || isPerPerson) {
    inventoryOptions = [
      { id: "unlimited", name: t("modal.inventoryModes.unlimited") },
      { id: "daily", name: t("modal.inventoryModes.perDay") },
    ];
  } else if (isPerUnit) {
    inventoryOptions = [
      { id: "unlimited", name: t("modal.inventoryModes.unlimited") },
      { id: "fixed", name: t("modal.inventoryModes.fixed") },
    ];
  }

  inventoryOptions.push({
    id: "contact",
    name: t("modal.inventoryModes.contact"),
  });

  useEffect(() => {
    const validModes = inventoryOptions.map((o) => o.id);
    const current = formData.productInventoryLimits.inventoryMode;

    if (onInventoryModeChange) {
      onInventoryModeChange(current);
    }

    if (isFixedPrice || isPerDay) {
      if (current !== "") {
        onChange("inventoryMode", "");
      }
    } else {
      if (typeof current !== "string" || !validModes.includes(current.trim())) {
        onChange("inventoryMode", validModes[0]);
      }
    }
  }, [priceTypeId, formData.productInventoryLimits.inventoryMode]);

  return (
    <section className="bg-white rounded-lg shadow p-6 space-y-6">
      {showInventoryFields && (
        <h2 className="text-xl font-bold text-primary">
          {t("modal.inventoryAndLimits")}
        </h2>
      )}

      {/* Disponibilité */}
      {showInventoryFields && (
        <div>
          <div className="flex items-center justify-start gap-2 mb-2">
            <h3 className="text-lg font-semibold text-primary">
              {t("modal.availability")}
            </h3>
            <InformationCircleIcon
              className="w-6 h-6 cursor-pointer"
              onClick={openTutorialModal}
            ></InformationCircleIcon>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {showInventoryFields && inventoryOptions.length > 0 && (
              <FormSelectBox
                label={t("modal.inventoryMode")}
                name="inventoryMode"
                value={
                  formData.productInventoryLimits.inventoryMode || "unlimited"
                }
                onChange={onChange}
                hideEmptySelection
                options={inventoryOptions}
                required
              />
            )}

            {formData.productInventoryLimits.inventoryMode !== "unlimited" &&
              formData.productInventoryLimits.inventoryMode !== "" &&
              !hasOptionQuantities &&
              formData.productInventoryLimits.inventoryMode !== "contact" && (
                <FormInput
                  label={t("modal.totalQuantity")}
                  name="totalQuantity"
                  value={formData.productInventoryLimits.totalQuantity}
                  onChange={(name, value) => onChange(name, parseInt(value))}
                  type="number"
                  min={0}
                />
              )}
          </div>
        </div>
      )}
      {/* Restrictions */}
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* <h3 className="text-lg font-semibold text-primary mb-2">
          {t("modal.limits")}{" "}
        </h3>

        
          <FormInput
            label={t("modal.maxDuration")}
            name="maxDuration"
            value={formData.productInventoryLimits.maxDuration || ""}
            onChange={(name, value) => onChange(name, parseInt(value))}
            type="number"
            min={0}
            help={t("modal.maxDurationHelp")}
          />
          */}
          {formData.productInventoryLimits.inventoryMode !== "contact" && (
            <FormSelectBox
              label={t("modal.returnMode")}
              name="returnPolicy"
              value={formData.productInventoryLimits.returnPolicy || "none"}
              onChange={onChange}
              hideEmptySelection
              options={[
                { id: "none", name: t("modal.noReturnNeeded") },
                { id: "client", name: t("modal.clientReturns") },
                { id: "provider", name: t("modal.providerRecovers") },
              ]}
            />
          )}
        </div>
      </div>
      <TutorialModal
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
        steps={tutorialSteps}
      />
    </section>
  );
};

export default InventorySection;
